"use client";

import Script from "next/script";
import { isSentinelValue } from "@/lib/env";

/**
 * Google global site tag (gtag.js) loader.
 *
 * Initialises the shared gtag.js runtime and configures both Google Ads
 * conversion tracking and Google Analytics 4 (GA4) in a single script load.
 * Either property ID may be absent — the script only configures the IDs that
 * are present, so you can enable GA4 independently of Google Ads.
 *
 * The script src URL uses whichever ID is available (Ads preferred), since
 * gtag.js is the same file regardless of the ID in the query string.
 *
 * Must be a Client Component so that `process.env.NEXT_PUBLIC_*` is inlined
 * into the client bundle (which entrypoint.sh replaces at container start),
 * rather than evaluated in a statically pre-rendered Server Component at
 * build time when the env vars still hold sentinel placeholder values.
 *
 * Only renders when at least one ID is set and we are not in a test
 * environment, so local/CI Playwright runs never load the real script.
 *
 * GA4 page views are sent page_view: false here — the analytics facade
 * (analytics.ga4.ts) handles them explicitly on route changes so SPA
 * navigation is tracked correctly.
 */
export const GoogleAdsTrackingScript = () => {
  const adsId = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
  const ga4Id = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;

  const resolvedAdsId = adsId && !isSentinelValue(adsId) ? adsId : null;
  const resolvedGa4Id = ga4Id && !isSentinelValue(ga4Id) ? ga4Id : null;

  if ((!resolvedAdsId && !resolvedGa4Id) || process.env.NODE_ENV === "test") {
    return null;
  }

  const scriptId = resolvedAdsId ?? resolvedGa4Id;

  const configLines = [
    resolvedAdsId ? `gtag('config', '${resolvedAdsId}');` : null,
    resolvedGa4Id ? `gtag('config', '${resolvedGa4Id}', { send_page_view: false });` : null,
  ]
    .filter(Boolean)
    .join("\n            ");

  return (
    <>
      <Script
        id="google-tags-loader"
        src={`https://www.googletagmanager.com/gtag/js?id=${scriptId}`}
        strategy="afterInteractive"
      />
      <Script
        id="google-tags-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            ${configLines}
          `,
        }}
      />
    </>
  );
};
